/* eslint-disable react/display-name */
import { Slot } from '@radix-ui/react-slot'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import React from 'react'

import { css } from 'design'
import { cn } from 'util/cn'

const tooltipStyles = css({
  backgroundColor: '$grey950',
  color: '$grey50',
  padding: '$2',
  borderRadius: '$3',
})

const tooltipArrow = css({
  fill: '$grey950',
})

export type TooltipProps = {
  tooltip?: React.ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: ((open: boolean) => void) | undefined
}

export const Tooltip = ({
  children,
  tooltip,
  open,
  defaultOpen = false,
  onOpenChange,
  ...props
}: React.PropsWithChildren<TooltipProps> &
  React.HTMLAttributes<HTMLDivElement>) => {

  return (
    <TooltipPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      <TooltipPrimitive.Trigger as={Slot}>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Content
        align="center"
        sideOffset={4}
        {...props}
        className={cn(
          props.className,
          tooltipStyles(),
        )}
      >
        {tooltip}
        <TooltipPrimitive.Arrow
          offset={5}
          width={11}
          height={5}
          className={tooltipArrow()}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Root>
  )
}

export type WithTooltipProps = {
  tooltip?: React.ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: ((open: boolean) => void) | undefined
}

export const withTooltip = <
  // eslint-disable-next-line @typescript-eslint/ban-types
  P extends object,
>(
  WrappedComponent:
    | React.ComponentClass<P>
    | React.ForwardRefExoticComponent<P>,
) =>
  React.forwardRef<unknown, WithTooltipProps & P>(
    ({ tooltip, open, defaultOpen, onOpenChange, ...rest }, ref) => {
      const wrappedProps = rest as P
      if (!tooltip) {
        return <WrappedComponent {...wrappedProps} ref={ref} />
      }

      return (
        <Tooltip
          tooltip={tooltip}
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
        >
          <WrappedComponent {...wrappedProps} ref={ref} />
        </Tooltip>
      )
    },
  )
